// src/components/admin/StoreRequests.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './StoreRequests.css';

const StoreRequests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(false);
  const [cloneInstructions, setCloneInstructions] = useState('');

  useEffect(() => {
    if (id) {
      fetchRequestDetails();
    } else {
      fetchRequests();
    }
  }, [filter, id]);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`https://creativepulse.pk/main-api/get-store-requests.php?status=${filter}`);
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestDetails = async () => {
    try {
      const response = await fetch(`https://creativepulse.pk/main-api/get-store-request.php?id=${id}`);
      const data = await response.json();
      if (data.success) {
        setSelectedRequest(data.request);
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedRequest) return;

    setUpdating(true);
    try {
      const response = await fetch('https://creativepulse.pk/main-api/update-request-status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedRequest.id, status })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Request ${status} successfully!`);
        if (status === 'approved') {
          generateCloneInstructions();
        } else {
          navigate('/admin/store-requests');
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const generateCloneInstructions = async () => {
    try {
      const response = await fetch('https://creativepulse.pk/main-api/generate-clone-instructions.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: selectedRequest.id })
      });
      const data = await response.json();
      if (data.success) {
        setCloneInstructions(data.instructions);
      }
    } catch (error) {
      console.error('Error generating instructions:', error);
    }
  };

  const markAsActivated = async () => {
    if (!window.confirm('Mark this store as activated? This will complete the process.')) {
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch('https://creativepulse.pk/main-api/mark-store-activated.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedRequest.id })
      });
      const data = await response.json();
      if (data.success) {
        alert('Store marked as activated successfully!');
        navigate('/admin/store-requests');
      } else {
        alert('Failed to mark as activated');
      }
    } catch (error) {
      console.error('Error marking as activated:', error);
      alert('Error occurred');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      approved: 'badge-approved',
      rejected: 'badge-rejected'
    };
    return badges[status] || 'badge-pending';
  };

  if (loading) return <div className="loader">Loading...</div>;

  // Detail View
  if (selectedRequest) {
    return (
      <div className="request-details">
        <div className="details-header">
          <button onClick={() => navigate('/admin/store-requests')} className="back-btn">
            ← Back to Requests
          </button>
          <h1>Store Request Details</h1>
        </div>

        <div className="details-card">
          <div className="details-section">
            <h3>Customer Information</h3>
            <div className="detail-row">
              <span className="label">Full Name:</span>
              <span className="value">{selectedRequest.full_name}</span>
            </div>
            <div className="detail-row">
              <span className="label">Phone Number:</span>
              <span className="value">{selectedRequest.owner_phone}</span>
            </div>
            <div className="detail-row">
              <span className="label">Address:</span>
              <span className="value">{selectedRequest.owner_address}</span>
            </div>
          </div>

          <div className="details-section">
            <h3>Store Information</h3>
            <div className="detail-row">
              <span className="label">Store Name:</span>
              <span className="value">{selectedRequest.store_name}</span>
            </div>
            <div className="detail-row">
              <span className="label">Domain Name:</span>
              <span className="value">{selectedRequest.domain_name}</span>
            </div>
            <div className="detail-row">
              <span className="label">Request Date:</span>
              <span className="value">{new Date(selectedRequest.created_at).toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className={`status-badge ${getStatusBadge(selectedRequest.status)}`}>
                {selectedRequest.status}
              </span>
            </div>
            {selectedRequest.clone_status === 'completed' && (
              <div className="detail-row">
                <span className="label">Site URL:</span>
                <span className="value">
                  <a href={selectedRequest.site_url} target="_blank" rel="noopener noreferrer">
                    {selectedRequest.site_url}
                  </a>
                </span>
              </div>
            )}
          </div>

          {/* Pending Actions */}
          {selectedRequest.status === 'pending' && (
            <div className="action-buttons">
              <button onClick={() => updateStatus('approved')} className="btn-approve" disabled={updating}>
                {updating ? 'Processing...' : '✓ Approve Request'}
              </button>
              <button onClick={() => updateStatus('rejected')} className="btn-reject" disabled={updating}>
                {updating ? 'Processing...' : '✗ Reject Request'}
              </button>
            </div>
          )}

          {/* Clone Instructions for Approved Requests */}
          {selectedRequest.status === 'approved' && selectedRequest.clone_status !== 'completed' && (
            <div className="clone-instructions">
              <h3>📋 Manual Deployment Instructions</h3>
              <div className="instructions-content">
                <p>Follow these steps to deploy the store:</p>
                <ol>
                  <li>Copy the demo site files from: <code>/public_html/demo-site.creativepulse.pk/</code></li>
                  <li>Create new folder: <code>/public_html/{selectedRequest.store_name.toLowerCase().replace(/\s+/g, '')}.creativepulse.pk/</code></li>
                  <li>Paste the copied files into the new folder</li>
                  <li>Create a new database: <code>u418243010_{selectedRequest.store_name.toLowerCase().replace(/\s+/g, '')}_db</code></li>
                  <li>Import the demo database structure</li>
                  <li>Update <code>config.php</code> with new database credentials</li>
                  <li>Update store settings (name, logo, etc.) in the database</li>
                  <li>Set up domain: <strong>{selectedRequest.domain_name}</strong> to point to the new folder</li>
                </ol>
                <div className="instruction-note">
                  <strong>💡 Tip:</strong> After completing these steps, click the button below to mark this store as activated.
                </div>
                <button onClick={markAsActivated} className="btn-activate" disabled={updating}>
                  {updating ? 'Processing...' : '✓ Mark as Activated'}
                </button>
              </div>
            </div>
          )}

          {/* Activated Store Info */}
          {selectedRequest.clone_status === 'completed' && (
            <div className="activated-info">
              <h3>✅ Store Activated</h3>
              <p>This store has been successfully deployed.</p>
              <p><strong>Site URL:</strong> <a href={selectedRequest.site_url} target="_blank" rel="noopener noreferrer">{selectedRequest.site_url}</a></p>
              <p><strong>Activated on:</strong> {new Date(selectedRequest.activated_at).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="store-requests">
      <div className="requests-header">
        <h1>Store Creation Requests</h1>
        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
          <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Pending</button>
          <button className={filter === 'approved' ? 'active' : ''} onClick={() => setFilter('approved')}>Approved</button>
          <button className={filter === 'rejected' ? 'active' : ''} onClick={() => setFilter('rejected')}>Rejected</button>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="no-requests">No store requests found</div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Store Name</th>
                <th>Owner Name</th>
                <th>Domain</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>{request.id}</td>
                  <td>{request.store_name}</td>
                  <td>{request.full_name}</td>
                  <td>{request.domain_name}</td>
                  <td>{request.owner_phone}</td>
                  <td>{new Date(request.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(request.status)}`}>
                      {request.status}
                      {request.clone_status === 'completed' && ' ✓'}
                    </span>
                  </td>
                  <td>
                    <button className="view-btn" onClick={() => navigate(`/admin/store-requests/${request.id}`)}>
                      View Details
                    </button>
                   </td>
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StoreRequests;